import type { FoodBlob, RemoteSnake, SelfState } from '../shared/protocol.js';
import {
  lookForId,
  segmentColor,
  type WormLook,
  type WormTemplate,
} from './wormLook.js';

export type RenderOpts = {
  testOrbs?: boolean;
  look?: WormLook;
  /** Draw only the screen-space overlays (vignette + minimap) on a clear canvas,
   *  used when the WebGL scene underneath owns the arena. */
  hudOnly?: boolean;
};

export type View = {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  camX: number;
  camY: number;
  zoom: number;
};

type Pt = { x: number; y: number; r: number };

const BG = '#08000a';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function makeView(canvas: HTMLCanvasElement): View {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  const view: View = { ctx, canvas, width: 1, height: 1, camX: 0, camY: 0, zoom: 0.78 };
  const resize = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    view.width = canvas.clientWidth;
    view.height = canvas.clientHeight;
    canvas.width = Math.floor(view.width * dpr);
    canvas.height = Math.floor(view.height * dpr);
  };
  resize();
  window.addEventListener('resize', resize);
  return view;
}

/**
 * Slither-style arena in ZETOP black and hot pink: overlapping body discs,
 * eyes, a sector grid, glowing orbs and a round minimap.
 */
export function render(
  view: View,
  self: SelfState | null,
  snakes: RemoteSnake[],
  food: FoodBlob[],
  worldRadius: number,
  timestamp: number,
  opts: RenderOpts = {},
): void {
  const { ctx, canvas } = view;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  view.width = canvas.clientWidth;
  view.height = canvas.clientHeight;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  if (opts.hudOnly) {
    ctx.clearRect(0, 0, view.width, view.height);
    drawVignette(ctx, view);
    if (self?.alive) drawMinimap(ctx, view, self, snakes, worldRadius);
    return;
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, view.width, view.height);

  if (self?.alive) {
    const targetZoom = Math.max(0.34, Math.min(0.92, 0.98 - self.radius / 58));
    view.zoom += (targetZoom - view.zoom) * 0.08;
    const lead = 150;
    const tx = self.x + Math.cos(self.angle) * lead;
    const ty = self.y + Math.sin(self.angle) * lead;
    if (Math.hypot(tx - view.camX, ty - view.camY) > 900) {
      view.camX = tx;
      view.camY = ty;
    } else {
      view.camX += (tx - view.camX) * 0.22;
      view.camY += (ty - view.camY) * 0.22;
    }
  } else if (self) {
    view.zoom += (0.82 - view.zoom) * 0.06;
    view.camX += (self.x - view.camX) * 0.08;
    view.camY += (self.y - view.camY) * 0.08;
  } else {
    view.zoom += (0.74 - view.zoom) * 0.04;
    view.camX = Math.cos(timestamp / 9000) * 140;
    view.camY = Math.sin(timestamp / 11000) * 80;
  }

  ctx.save();
  ctx.translate(view.width / 2, view.height / 2);
  ctx.scale(view.zoom, view.zoom);
  ctx.translate(-view.camX, -view.camY);

  drawArena(ctx, view, worldRadius);
  noteJuice(self);

  const look = opts.look ?? {
    a: '#ff2d95', b: '#ffb3d9', paint: 'solid' as const, template: 'classic' as const,
  };
  const preview = !self && snakes.length === 0;
  if (preview) drawPreview(ctx, timestamp);
  else {
    drawFood(ctx, food, timestamp, opts.testOrbs !== false);
    for (const snake of snakes) drawSnake(ctx, snake, timestamp);
    if (self?.alive) {
      drawBoost(ctx, self, timestamp);
      drawSelf(ctx, self, look, timestamp);
    }
  }

  ctx.restore();

  drawVignette(ctx, view);
  if (self?.alive) drawMinimap(ctx, view, self, snakes, worldRadius);
}

function drawArena(ctx: CanvasRenderingContext2D, view: View, worldRadius: number): void {
  ctx.fillStyle = '#17020f';
  ctx.beginPath();
  ctx.arc(0, 0, worldRadius, 0, Math.PI * 2);
  ctx.fill();

  const halfW = view.width / 2 / view.zoom + 160;
  const halfH = view.height / 2 / view.zoom + 160;
  const gap = 150;
  const x0 = Math.floor((view.camX - halfW) / gap);
  const x1 = Math.ceil((view.camX + halfW) / gap);
  const y0 = Math.floor((view.camY - halfH) / gap);
  const y1 = Math.ceil((view.camY + halfH) / gap);
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255, 45, 149, 0.16)';
  ctx.fillStyle = 'rgba(255, 122, 184, 0.55)';
  for (let gx = x0; gx <= x1; gx++) {
    for (let gy = y0; gy <= y1; gy++) {
      const px = gx * gap + Math.sin(gy * 7.1) * 26;
      const py = gy * gap + Math.cos(gx * 5.3) * 26;
      if (Math.hypot(px, py) > worldRadius) continue;
      const qx = (gx + 1) * gap + Math.sin(gy * 7.1) * 26;
      const qy = gy * gap + Math.cos((gx + 1) * 5.3) * 26;
      const rx = gx * gap + Math.sin((gy + 1) * 7.1) * 26;
      const ry = (gy + 1) * gap + Math.cos(gx * 5.3) * 26;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(qx, qy);
      ctx.moveTo(px, py);
      ctx.lineTo(rx, ry);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(px, py, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.save();
  ctx.fillStyle = '#08000a';
  ctx.beginPath();
  ctx.rect(-worldRadius - 8000, -worldRadius - 8000, worldRadius * 2 + 16000, worldRadius * 2 + 16000);
  ctx.arc(0, 0, worldRadius, 0, Math.PI * 2);
  ctx.fill('evenodd');
  ctx.shadowColor = '#ff2d95';
  ctx.shadowBlur = reduceMotion ? 0 : 28;
  ctx.strokeStyle = '#ff2d95';
  ctx.lineWidth = 8;
  ctx.beginPath();
  ctx.arc(0, 0, worldRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

const sprites = new Map<string, HTMLCanvasElement>();

function orbSprite(hue: number): HTMLCanvasElement {
  const h = ((Math.round(hue) % 360) + 360) % 360;
  const key = `hue:${h}`;
  const cached = sprites.get(key);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const g = canvas.getContext('2d');
  if (!g) return canvas;
  const glow = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  glow.addColorStop(0, '#ffffff');
  glow.addColorStop(0.14, `hsl(${h}, 100%, 93%)`);
  glow.addColorStop(0.4, `hsla(${h}, 95%, 60%, 0.95)`);
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = glow;
  g.beginPath();
  g.arc(32, 32, 32, 0, Math.PI * 2);
  g.fill();
  sprites.set(key, canvas);
  return canvas;
}

function paintOrb(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, hue: number, phase: number): void {
  const pulse = reduceMotion ? 1 : 1 + Math.sin(phase) * 0.14;
  const glow = (10 + Math.max(0.4, size) * 8) * pulse;
  ctx.drawImage(orbSprite(hue), x - glow, y - glow, glow * 2, glow * 2);
}

function drawFood(ctx: CanvasRenderingContext2D, food: FoodBlob[], timestamp: number, testOrbs: boolean): void {
  for (let i = 0; i < food.length; i++) {
    const blob = food[i]!;
    const hue = blob[2];
    const size = blob[3] > 0 ? blob[3] : 2;
    if (testOrbs) {
      const pulse = reduceMotion ? 1 : 1 + Math.sin(timestamp * 0.004 + blob[0]) * 0.15;
      const color = `hsl(${hue}, 95%, 62%)`;
      const r = (3.2 + size * 1.6) * pulse * 1.5;
      ctx.fillStyle = color;
      ctx.shadowColor = reduceMotion ? 'transparent' : color;
      ctx.shadowBlur = reduceMotion ? 0 : 14;
      ctx.beginPath();
      ctx.arc(blob[0], blob[1], r, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#0a0006';
      ctx.stroke();
    } else {
      paintOrb(ctx, blob[0], blob[1], size, hue, timestamp / 320 + i);
    }
  }
  ctx.shadowBlur = 0;
}

type PreviewSnake = { hue: number; phase: number; speed: number; r: number; len: number };

const previewSnakes: PreviewSnake[] = [
  { hue: 338, phase: 0.4, speed: 0.00062, r: 20, len: 42 },
  { hue: 42, phase: 1.7, speed: 0.00048, r: 16, len: 30 },
  { hue: 198, phase: 2.8, speed: 0.00054, r: 17, len: 34 },
  { hue: 128, phase: 4.1, speed: 0.00044, r: 15, len: 26 },
  { hue: 278, phase: 5.2, speed: 0.0005, r: 16, len: 30 },
  { hue: 18, phase: 0.9, speed: 0.00046, r: 22, len: 48 },
];

function previewPoints(snake: PreviewSnake, timestamp: number): Pt[] {
  const pts: Pt[] = [];
  const base = timestamp * snake.speed + snake.phase;
  for (let i = 0; i < snake.len; i++) {
    const u = base - i * 0.105;
    pts.push({
      x: Math.sin(u) * 1080 + Math.cos(u * 0.37 + snake.phase) * 240,
      y: Math.cos(u * 0.82 + snake.phase) * 620 + Math.sin(u * 1.6) * 90,
      r: snake.r * (1 - (i / snake.len) * 0.48),
    });
  }
  return pts;
}

let juice = 0;
let lastSegments = 0;

function noteJuice(self: SelfState | null): void {
  const segments = self?.alive ? self.segments : 0;
  if (self?.alive && segments > lastSegments) juice = 1;
  if (!self?.alive) juice = 0;
  lastSegments = segments;
  juice *= reduceMotion ? 0 : 0.9;
}

function drawPreview(ctx: CanvasRenderingContext2D, timestamp: number): void {
  for (const snake of previewSnakes) {
    const pts = previewPoints(snake, timestamp);
    drawWorm(ctx, pts, lookForId(`preview-${snake.hue}`), timestamp, heading(pts));
  }
}

function drawSnake(ctx: CanvasRenderingContext2D, snake: RemoteSnake, timestamp: number): void {
  const pts = triples(snake.body);
  if (pts.length === 0) return;
  const head = pts[0]!;
  drawWorm(ctx, pts, lookForId(snake.id), timestamp, heading(pts));
  label(ctx, snake.name, head.x, head.y - head.r - 18);
  if (snake.emote) label(ctx, snake.emote, head.x, head.y - head.r - 36, '#ffd0df');
}

function drawSelf(ctx: CanvasRenderingContext2D, self: SelfState, look: WormLook, timestamp: number): void {
  const pts = sampleSelf(self);
  const head = pts[0];
  if (!head) return;
  if (juice > 0.05) {
    ctx.strokeStyle = `rgba(255, 240, 248, ${juice})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(head.x, head.y, head.r * (2.2 + (1 - juice)), 0, Math.PI * 2);
    ctx.stroke();
  }
  drawWorm(ctx, pts, look, timestamp, self.angle);
  if (self.emote) label(ctx, self.emote, head.x, head.y - head.r - 18, '#ffd0df');
}

function drawWorm(
  ctx: CanvasRenderingContext2D,
  pts: Pt[],
  look: WormLook,
  timestamp: number,
  angle: number,
): void {
  const n = pts.length;
  if (n === 0) return;
  const template: WormTemplate = look.template;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  for (let i = n - 1; i >= 0; i--) {
    const p = pts[i]!;
    const next = pts[i + 1];
    const prev = pts[i - 1];
    const col = segmentColor(look, i, n, reduceMotion ? 0 : timestamp);
    const r = Math.max(4, p.r);
    const nb = next ?? prev ?? p;
    const aim = Math.atan2(p.y - nb.y, p.x - nb.x);
    ctx.shadowColor = reduceMotion ? 'transparent' : col;
    ctx.shadowBlur = reduceMotion ? 0 : 14;
    ctx.fillStyle = col;
    ctx.strokeStyle = col;
    ctx.globalAlpha = 0.78 + (1 - i / n) * 0.22;
    if (template === 'neural') {
      if (next) {
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(next.x, next.y);
        ctx.stroke();
      }
      const hop = pts[i + 3];
      if (hop) {
        ctx.lineWidth = 1;
        ctx.globalAlpha *= 0.6;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(hop.x, hop.y);
        ctx.stroke();
        ctx.globalAlpha /= 0.6;
      }
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 0.55 * (i % 4 ? 1 : 1.6), 0, Math.PI * 2);
      ctx.fill();
    } else if (template === 'ribbon') {
      if (next) {
        ctx.lineWidth = r * 1.7;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(next.x, next.y);
        ctx.stroke();
      }
    } else if (template === 'thorn') {
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      if (i % 3 === 0 && i > 0) {
        for (const g of [1, -1]) {
          const px = Math.cos(aim + g * 1.571);
          const py = Math.sin(aim + g * 1.571);
          const dx = Math.cos(aim) * 4;
          const dy = Math.sin(aim) * 4;
          ctx.beginPath();
          ctx.moveTo(p.x + px * r * 0.6 + dx, p.y + py * r * 0.6 + dy);
          ctx.lineTo(p.x + px * (r + 10), p.y + py * (r + 10));
          ctx.lineTo(p.x + px * r * 0.6 - dx, p.y + py * r * 0.6 - dy);
          ctx.fill();
        }
      }
    } else if (template === 'pixel') {
      ctx.fillRect(p.x - r * 0.8, p.y - r * 0.8, r * 1.6, r * 1.6);
    } else if (template === 'bubble') {
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(p.x - r * 0.3, p.y - r * 0.3, r * 0.22, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff';
      ctx.globalAlpha = 0.12 * (1 - i / n);
      ctx.beginPath();
      ctx.arc(p.x - r * 0.25, p.y - r * 0.25, r * 0.45, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
  const head = pts[0]!;
  const sc = Math.max(0.7, head.r / 10);
  for (const o of [-0.6, 0.6]) {
    const ex = head.x + Math.cos(angle + o) * 8 * sc;
    const ey = head.y + Math.sin(angle + o) * 8 * sc;
    ctx.fillStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = reduceMotion ? 0 : 6;
    ctx.beginPath();
    ctx.arc(ex, ey, 3.6 * sc, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#2a0219';
    ctx.beginPath();
    ctx.arc(ex + Math.cos(angle) * 1.4, ey + Math.sin(angle) * 1.4, 1.8 * sc, 0, Math.PI * 2);
    ctx.fill();
  }
}

const puffs: Array<{ x: number; y: number; life: number; r: number }> = [];

function drawBoost(ctx: CanvasRenderingContext2D, self: SelfState, timestamp: number): void {
  if (self.boosting && Math.floor(timestamp / 40) !== Math.floor((timestamp - 16) / 40)) {
    puffs.push({
      x: self.x - Math.cos(self.angle) * self.radius * 1.6,
      y: self.y - Math.sin(self.angle) * self.radius * 1.6,
      life: 1,
      r: self.radius * 0.55,
    });
  }
  for (let i = puffs.length - 1; i >= 0; i--) {
    const puff = puffs[i]!;
    puff.life -= 0.035;
    if (puff.life <= 0) {
      puffs.splice(i, 1);
      continue;
    }
    ctx.fillStyle = `rgba(255, 180, 210, ${puff.life * 0.45})`;
    ctx.beginPath();
    ctx.arc(puff.x, puff.y, puff.r * (1.6 - puff.life), 0, Math.PI * 2);
    ctx.fill();
  }
  if (puffs.length > 70) puffs.splice(0, puffs.length - 70);
}

function heading(pts: Pt[]): number {
  const head = pts[0];
  const next = pts[1] ?? head;
  if (!head || !next) return 0;
  return Math.atan2(head.y - next.y, head.x - next.x);
}

function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fill = '#fff'): void {
  ctx.font = '800 15px Inter, ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(12, 0, 4, 0.85)';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

function triples(body: number[]): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i + 2 < body.length; i += 3) {
    out.push({ x: body[i]!, y: body[i + 1]!, r: body[i + 2]! / 10 });
  }
  return out;
}

const headHistory: Array<{ x: number; y: number }> = [];

function sampleSelf(self: SelfState): Pt[] {
  const last = headHistory[headHistory.length - 1];
  if (!last || Math.hypot(self.x - last.x, self.y - last.y) > 1.2) {
    headHistory.push({ x: self.x, y: self.y });
    if (headHistory.length > 1200) headHistory.shift();
  }
  const spacing = Math.max(5, self.radius * 0.58);
  const out: Pt[] = [{ x: self.x, y: self.y, r: self.radius * 1.16 }];
  let prevX = self.x;
  let prevY = self.y;
  let carry = 0;
  for (let i = headHistory.length - 1; i >= 0 && out.length < 110; i--) {
    const p = headHistory[i]!;
    let dx = prevX - p.x;
    let dy = prevY - p.y;
    let dist = Math.hypot(dx, dy);
    if (dist < 1e-6) continue;
    while (carry + dist >= spacing && out.length < 110) {
      const need = spacing - carry;
      const t = need / dist;
      prevX -= dx * t;
      prevY -= dy * t;
      const k = out.length / 110;
      out.push({ x: prevX, y: prevY, r: self.radius * (1.05 - k * 0.5) });
      dx = prevX - p.x;
      dy = prevY - p.y;
      dist = Math.hypot(dx, dy);
      carry = 0;
    }
    carry += dist;
    prevX = p.x;
    prevY = p.y;
  }
  return out;
}

export function resetTrail(): void {
  headHistory.length = 0;
  puffs.length = 0;
}

function drawVignette(ctx: CanvasRenderingContext2D, view: View): void {
  const g = ctx.createRadialGradient(
    view.width / 2, view.height / 2, view.width * 0.2,
    view.width / 2, view.height / 2, view.width * 0.75,
  );
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.38)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, view.width, view.height);
}

function drawMinimap(
  ctx: CanvasRenderingContext2D,
  view: View,
  self: SelfState,
  snakes: RemoteSnake[],
  worldRadius: number,
): void {
  const radius = Math.min(84, view.width * 0.14);
  const cx = 18 + radius;
  const cy = view.height - 18 - radius;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(6, 0, 2, 0.8)';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255, 45, 107, 0.9)';
  ctx.stroke();
  ctx.clip();
  const scale = (radius - 8) / worldRadius;
  const dot = (wx: number, wy: number, color: string, r: number): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx + wx * scale, cy + wy * scale, r, 0, Math.PI * 2);
    ctx.fill();
  };
  for (const snake of snakes) {
    const hx = snake.body[0];
    const hy = snake.body[1];
    if (hx === undefined || hy === undefined) continue;
    dot(hx, hy, `hsl(${snake.color.h} 80% 64%)`, 2.6);
  }
  dot(self.x, self.y, '#ff2d6b', 3.6);
  ctx.restore();
}
